import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import TechLayout from '../../components/tech/TechLayout';
import { getWorkOrder, techUpdateWorkOrder } from '../../api/client';

const STATUS_COLORS = {
  pending: { bg: '#FEF3C7', color: '#92400E' },
  assigned: { bg: '#DBEAFE', color: '#1E40AF' },
  in_progress: { bg: '#EDE9FE', color: '#5B21B6' },
  completed: { bg: '#D1FAE5', color: '#065F46' },
  cancelled: { bg: '#F3F4F6', color: '#6B7280' },
};

function StatusPill({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.cancelled;
  return (
    <span
      style={{
        ...s,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {status?.replace('_', ' ') || 'unknown'}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 14,
        marginBottom: 14,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid #F3F4F6',
          fontSize: 12,
          fontWeight: 700,
          color: '#6B7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {title}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#111827' }}>{value || '—'}</div>
    </div>
  );
}

export default function TechJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getWorkOrder(id);
      setJob(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const updateStatus = async (status) => {
    if (!job) return;
    try {
      setSaving(true);
      const res = await techUpdateWorkOrder(id, { status });
      setJob(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TechLayout title="Job Detail">
      <button
        onClick={() => navigate('/tech/jobs')}
        style={{
          border: 'none',
          background: '#F3F4F6',
          color: '#374151',
          borderRadius: 10,
          padding: '9px 12px',
          fontWeight: 700,
          cursor: 'pointer',
          marginBottom: 14,
        }}
      >
        ← Back to Jobs
      </button>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#6B7280', padding: '40px 0' }}>Loading job...</div>
      ) : !job ? (
        <div style={{ color: '#DC2626' }}>Job not found.</div>
      ) : (
        <>
          <div
            style={{
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 14,
              padding: 14,
              marginBottom: 14,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
                  {job.job_number || `WO-${job.id}`}
                </div>
                <div style={{ fontSize: 14, color: '#374151', marginTop: 4 }}>
                  {job.customer_name || 'Unknown Customer'}
                </div>
              </div>
              <StatusPill status={job.status} />
            </div>
          </div>

          <Section title="Basic Information">
            <InfoRow label="Customer Name" value={job.customer_name} />
            <InfoRow label="Phone" value={job.customer_phone} />
            <InfoRow label="Address" value={[job.address, job.city, job.state_province, job.zipcode, job.country].filter(Boolean).join(', ')} />
            <InfoRow label="Scheduled At" value={job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : ''} />
            <InfoRow label="Priority" value={job.priority} />
          </Section>

          <Section title="Unit Information">
            <InfoRow label="Equipment Type" value={job.equipment_type} />
            <InfoRow label="Brand" value={job.equipment_brand} />
            <InfoRow label="Model" value={job.equipment_model} />
            <InfoRow label="Serial Number" value={job.equipment_serial} />
            <InfoRow label="Unit Description" value={job.unit_description} />
          </Section>

          <Section title="Warranty Information">
            <InfoRow label="Warranty Status" value={job.warranty_status} />
            <InfoRow label="Extended Warranty" value={job.extended_warranty} />
            <InfoRow label="Invoice Date" value={job.invoice_date} />
          </Section>

          <Section title="Problem Description">
            <div style={{ fontSize: 14, color: '#111827', whiteSpace: 'pre-wrap' }}>
              {job.problem_description || '—'}
            </div>
          </Section>

          <Section title="Technician Actions">
            <div style={{ display: 'grid', gap: 10 }}>

              {/* Accept Job — only show when status is assigned */}
              {job.status === 'assigned' && (
                <button
                  disabled={saving}
                  onClick={() => updateStatus('in_progress')}
                  style={{
                    border: 'none',
                    background: '#7C3AED',
                    color: '#fff',
                    borderRadius: 12,
                    padding: '14px',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: 'pointer',
                  }}
                >
                  {saving ? 'Accepting...' : '✅ Accept Job'}
                </button>
              )}

              {/* Navigation */}
              <div>
                <button
                  onClick={() => setShowMapPicker(p => !p)}
                  style={{
                    width: '100%',
                    border: '1px solid #2563EB',
                    background: '#fff',
                    color: '#2563EB',
                    borderRadius: showMapPicker ? '12px 12px 0 0' : 12,
                    padding: '12px 14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  🧭 Navigate to Customer
                </button>
                {showMapPicker && job?.address && (
                  <div style={{ border: '1px solid #BFDBFE', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([job.address, job.city, job.state_province, job.zipcode, job.country].filter(Boolean).join(', '))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block', padding: '12px 8px', textAlign: 'center', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: 13, textDecoration: 'none', borderRight: '1px solid #BFDBFE' }}
                    >
                      Google Maps
                    </a>
                    <a
                      href={`maps://maps.apple.com/?daddr=${encodeURIComponent([job.address, job.city, job.state_province, job.zipcode, job.country].filter(Boolean).join(', '))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block', padding: '12px 8px', textAlign: 'center', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}
                    >
                      Apple Maps
                    </a>
                  </div>
                )}
                {showMapPicker && !job?.address && (
                  <div style={{ border: '1px solid #BFDBFE', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '10px 14px', background: '#FEF9C3', color: '#92400E', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                    No address on this job
                  </div>
                )}
              </div>

              {/* Arrived / Start Diagnosis — with Diagnosis Notes + Photos sub-actions */}
              <div style={{ border: '2px solid #2563EB', borderRadius: 12, overflow: 'hidden' }}>
                <button
                  disabled={saving}
                  onClick={async () => {
                    if (job.status !== 'in_progress') await updateStatus('in_progress');
                    navigate(`/tech/jobs/${id}/notes`);
                  }}
                  style={{
                    width: '100%',
                    border: 'none',
                    background: '#2563EB',
                    color: '#fff',
                    padding: '12px 14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  Arrived / Start Diagnosis
                </button>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid #93C5FD' }}>
                  <Link
                    to={`/tech/jobs/${id}/notes`}
                    style={{
                      textDecoration: 'none',
                      padding: '10px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#1D4ED8',
                      background: '#EFF6FF',
                      textAlign: 'center',
                      borderRight: '1px solid #93C5FD',
                    }}
                  >
                    📋 Diagnosis Notes
                  </Link>
                  <Link
                    to={`/tech/jobs/${id}/photos`}
                    style={{
                      textDecoration: 'none',
                      padding: '10px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#1D4ED8',
                      background: '#EFF6FF',
                      textAlign: 'center',
                    }}
                  >
                    📷 Photos
                  </Link>
                </div>
              </div>

              <button
                disabled={saving}
                onClick={() => navigate(`/tech/jobs/${id}/parts`)}
                style={{
                  border: 'none',
                  background: '#D97706',
                  color: '#fff',
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Request / Waiting Parts
              </button>

              <button
                disabled={saving}
                onClick={() => navigate(`/tech/jobs/${id}/complete`)}
                style={{
                  border: 'none',
                  background: '#059669',
                  color: '#fff',
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Complete Job
              </button>
            </div>
          </Section>
        </>
      )}
    </TechLayout>
  );
}